(() => {
  const money = (cents, format) => {
    try {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, format || window.theme?.moneyFormat);
      }
    } catch (e) {
      /* fall through */
    }
    const n = (Number(cents) || 0) / 100;
    return `$${n.toFixed(2)}`;
  };

  class FurnitureProductBuy extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.moneyFormat = this.dataset.moneyFormat || '';
      this.slides = [...this.querySelectorAll('[data-fpb-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-fpb-thumb]')];
      this.index = 0;
      this.selectedUpgrades = new Set();
      this.selectedOptions = {};
      this.variants = this.readVariants();
      this.optionButtons = [...this.querySelectorAll('[data-fpb-option-btn]')];
      this.variantInput = this.querySelector('[data-fpb-variant-id]');
      this.priceEl = this.querySelector('[data-fpb-price]');
      this.compareEl = this.querySelector('[data-fpb-compare]');
      this.promoEl = this.querySelector('[data-fpb-promo]');
      this.discountPctEl = this.querySelector('[data-fpb-discount-pct]');
      this.atc = this.querySelector('[data-fpb-atc]');
      this.form = this.querySelector('[data-fpb-form]');
      this.qtyInput = this.querySelector('[data-fpb-qty]');
      this.qtyHidden = this.querySelector('[data-fpb-qty-hidden]');
      this.qtyMinus = this.querySelector('[data-fpb-qty-minus]');
      this.qtyPlus = this.querySelector('[data-fpb-qty-plus]');
      this.stockEl = this.querySelector('[data-fpb-stock]');
      this.overlay = this.querySelector('[data-fpb-overlay]');

      this.optionButtons.forEach((btn) => {
        if (btn.classList.contains('is-active') || btn.getAttribute('aria-pressed') === 'true') {
          this.selectedOptions[btn.dataset.optionName] = btn.dataset.optionValue;
        }
        btn.addEventListener('click', () => this.onOptionClick(btn));
      });

      this.querySelector('[data-fpb-prev]')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.go(-1);
      });
      this.querySelector('[data-fpb-next]')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.go(1);
      });
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.show(Number(thumb.dataset.index) || 0);
        });
      });

      // Keep gallery interactive even if slides were empty at first paint
      this.refreshSlides();
      this.show(this.index);

      this.qtyMinus?.addEventListener('click', () => this.setQty(this.getQty() - 1));
      this.qtyPlus?.addEventListener('click', () => this.setQty(this.getQty() + 1));

      this.querySelectorAll('[data-fpb-upgrade]').forEach((btn) => {
        btn.addEventListener('click', () => this.toggleUpgrade(btn));
      });

      this.querySelectorAll('[data-fpb-open]').forEach((btn) => {
        btn.addEventListener('click', () => this.openDrawer(btn.dataset.fpbOpen));
      });
      this.querySelectorAll('[data-fpb-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeDrawer());
      });

      this.querySelectorAll('[data-fpb-scroll-to]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.fpbScrollTo;
          if (!id) return;
          document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });

      this.querySelector('[data-fpb-reviews]')?.addEventListener('click', () => {
        const id =
          this.querySelector('[data-fpb-reviews]')?.dataset.fpbScrollTo ||
          'product-reviews-widget-anchor';
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      this.form?.addEventListener('submit', (e) => this.onSubmit(e));
      this.syncVariantUI();
      this.updateAtcLabel();
    }

    readVariants() {
      const node = this.querySelector('[data-fpb-variants]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent || '[]');
      } catch (e) {
        return [];
      }
    }

    refreshSlides() {
      this.slides = [...this.querySelectorAll('[data-fpb-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-fpb-thumb]')];
    }

    show(i) {
      this.refreshSlides();
      if (!this.slides.length) return;
      this.index = ((i % this.slides.length) + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, n) => {
        const on = n === this.index;
        slide.classList.toggle('is-active', on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      this.thumbs.forEach((thumb, n) => thumb.classList.toggle('is-active', n === this.index));
    }

    go(delta) {
      this.refreshSlides();
      this.show(this.index + delta);
    }

    showMediaId(mediaId) {
      if (!mediaId) return;
      const i = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
      if (i >= 0) this.show(i);
    }

    onOptionClick(btn) {
      const name = btn.dataset.optionName;
      const value = btn.dataset.optionValue;
      this.selectedOptions[name] = value;

      this.querySelectorAll(`[data-option-name="${CSS.escape(name)}"]`).forEach((el) => {
        const active = el.dataset.optionValue === value;
        el.classList.toggle('is-active', active);
        el.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      const group = btn.closest('[data-fpb-option]');
      const current = group?.querySelector('[data-fpb-option-current]');
      if (current) current.textContent = value;

      this.syncVariantUI();
    }

    findVariant() {
      const ordered = [...this.querySelectorAll('[data-fpb-option]')].map((el) => el.dataset.fpbOption);
      return this.variants.find((variant) =>
        ordered.every((name, index) => {
          const wanted = this.selectedOptions[name];
          if (!wanted) return true;
          return String(variant[`option${index + 1}`] || '') === String(wanted);
        })
      );
    }

    syncVariantUI() {
      const variant = this.findVariant() || this.variants[0];
      if (!variant) return;

      if (this.variantInput) this.variantInput.value = variant.id;

      if (this.priceEl) this.priceEl.textContent = money(variant.price, this.moneyFormat);

      const compare = Number(variant.compare_at_price) || 0;
      const onSale = compare > variant.price;
      if (this.compareEl) {
        this.compareEl.classList.toggle('is-hidden', !onSale);
        this.compareEl.textContent = onSale ? money(compare, this.moneyFormat) : '';
      }
      if (this.promoEl) {
        this.promoEl.classList.toggle('is-hidden', !onSale);
        if (onSale && this.discountPctEl) {
          const pct = Math.round(((compare - variant.price) / compare) * 100);
          this.discountPctEl.textContent = `${pct}% off`;
        }
      }

      if (this.atc) {
        this.atc.disabled = !variant.available;
      }
      this.stockEl?.classList.toggle('is-hidden', !variant.available);

      if (variant.featured_media_id) {
        this.showMediaId(variant.featured_media_id);
      }

      this.updateAtcLabel();
    }

    getQty() {
      return Math.max(1, parseInt(this.qtyInput?.value || '1', 10) || 1);
    }

    setQty(n) {
      const qty = Math.max(1, n);
      if (this.qtyInput) this.qtyInput.value = String(qty);
      if (this.qtyHidden) this.qtyHidden.value = String(qty);
      if (this.qtyMinus) this.qtyMinus.disabled = qty <= 1;
      this.updateAtcLabel();
    }

    toggleUpgrade(btn) {
      if (btn.disabled || btn.dataset.available === 'false') return;
      const id = String(btn.dataset.variantId);
      const selected = this.selectedUpgrades.has(id);
      if (selected) this.selectedUpgrades.delete(id);
      else this.selectedUpgrades.add(id);

      this.querySelectorAll(`[data-fpb-upgrade][data-variant-id="${CSS.escape(id)}"]`).forEach((el) => {
        el.classList.toggle('is-selected', !selected);
      });
      this.updateAtcLabel();
    }

    upgradesTotal() {
      let total = 0;
      this.selectedUpgrades.forEach((id) => {
        const btn = this.querySelector(`[data-fpb-upgrade][data-variant-id="${CSS.escape(id)}"]`);
        total += Number(btn?.dataset.price || 0);
      });
      return total;
    }

    updateAtcLabel() {
      if (!this.atc) return;
      const variant = this.findVariant() || this.variants[0];
      if (!variant) return;
      if (!variant.available) {
        this.atc.textContent = 'Sold out';
        return;
      }
      const label = this.atc.dataset.label || 'Add to cart';
      const total = (Number(variant.price) || 0) * this.getQty() + this.upgradesTotal();
      this.atc.textContent = `${label} - ${money(total, this.moneyFormat)}`;
    }

    openDrawer(name) {
      if (!this.overlay || !name) return;
      this.overlay.hidden = false;
      this.querySelectorAll('[data-fpb-drawer]').forEach((drawer) => {
        drawer.hidden = drawer.dataset.fpbDrawer !== name;
      });
      document.body.classList.add('fpb-drawer-open');
    }

    closeDrawer() {
      if (!this.overlay) return;
      this.overlay.hidden = true;
      this.querySelectorAll('[data-fpb-drawer]').forEach((drawer) => {
        drawer.hidden = true;
      });
      document.body.classList.remove('fpb-drawer-open');
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.atc || this.atc.disabled) return;

      const variant = this.findVariant() || this.variants[0];
      if (!variant?.available) return;

      const items = [{ id: variant.id, quantity: this.getQty() }];
      this.selectedUpgrades.forEach((id) => {
        items.push({ id: Number(id), quantity: 1 });
      });

      this.atc.classList.add('is-loading');
      const root = window.Shopify?.routes?.root || '/';
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error('Add to cart failed');

        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:updated'));
        if (window.theme?.cartDrawer?.open) {
          window.theme.cartDrawer.open();
        } else {
          window.location.href = `${root}cart`;
        }
      } catch (e) {
        // Fallback: submit main product only via native form
        if (this.form) this.form.submit();
      } finally {
        this.atc.classList.remove('is-loading');
      }
    }
  }

  if (!customElements.get('furniture-product-buy')) {
    customElements.define('furniture-product-buy', FurnitureProductBuy);
  }
})();
